import {
  ExperienceAnchor,
  ExperienceAnchorType
} from '@dadohr/shared/domainTypes/experience';
import { UnsafeTFunction } from '../../i18next';
import { ONE_DAY } from '../time';
import { getFixedDateForStart, getMilestoneOffsetDays } from './anchorMode';

export type WeekLabel = {
  /** Label for a column with room for it, e.g. `Week -1` or `09/30-10/06`. */
  full: string;
  /** Label for a narrow column; only differs for calendar-date experiences (`09/30`). */
  short: string;
};

const same = (label: string): WeekLabel => ({ full: label, short: label });

/**
 * `MM/DD` when the org's date format puts the month before the day (covers
 * `MM-DD-YYYY`, `M/D/YYYY` and year-first formats), otherwise `DD/MM`.
 */
const dayFormatFor = (orgDateFormat: string) => {
  const f = orgDateFormat.toUpperCase();
  return f.indexOf('M') < f.indexOf('D') ? 'MM/DD' : 'DD/MM';
};

/**
 * Header label for the week column whose first day is `weekStartDay` (day 0 =
 * experience start), phrased for the experience's start setting:
 *
 * - milestone, experience starts before it: weeks count from the column that
 *   contains the milestone day (Week 1); earlier columns are Week -1, -2, …
 * - milestone, experience starts on or after it, or starts on enrollment:
 *   Week 1, 2, … from the experience start
 * - calendar date: the column's first and last dates
 *
 * Columns themselves are always 7-day groups aligned to day 0; only the text
 * changes. `orgDateFormat` is the org's default date format (a moment format
 * such as `MM-DD-YYYY`) and only decides day/month order.
 *
 * Columns are fixed to the experience start, so on a milestone experience
 * Week 1 is the whole column containing the milestone day, not the seven
 * days from it – ie, no change to how columns are calculated. The participant
 * app's week navigation counts from the milestone day itself, so when the
 * offset is not a whole number of weeks the two number the same days
 * differently: with a 10-day lead, days 7-9 are Week 1 here and Week -1 there.
 * They agree for whole-week offsets. Deliberate decision, because the use
 * cases are different.
 */
export const getWeekLabel = (
  anchor: ExperienceAnchor,
  tz: string,
  orgDateFormat: string,
  weekStartDay: number,
  t: UnsafeTFunction
): WeekLabel => {
  if (anchor.type === ExperienceAnchorType.FIXED) {
    const format = dayFormatFor(orgDateFormat);
    const start = getFixedDateForStart(anchor.ts, weekStartDay * ONE_DAY, tz);
    const end = start.clone().add(6, 'days');
    return {
      full: `${start.format(format)}-${end.format(format)}`,
      short: start.format(format)
    };
  }

  // Column index of the week that counts as Week 1: the column containing the
  // milestone when the experience starts before it, otherwise the first column.
  const offsetDays = getMilestoneOffsetDays(anchor);
  const firstWeekColumn =
    offsetDays !== null && offsetDays < 0 ? Math.floor(-offsetDays / 7) : 0;
  const relative = Math.floor(weekStartDay / 7) - firstWeekColumn;
  // No week 0: the column before Week 1 is Week -1.
  const week = relative >= 0 ? relative + 1 : relative;
  return same(t('Week {{ week }}', { week }));
};
